import { base44 } from "@/api/base44Client";

function urlBase64ToUint8Array(base64String) {
  const padding = "=".repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, "+").replace(/_/g, "/");
  const rawData = window.atob(base64);
  const outputArray = new Uint8Array(rawData.length);
  for (let i = 0; i < rawData.length; ++i) {
    outputArray[i] = rawData.charCodeAt(i);
  }
  return outputArray;
}

export function isPushSupported() {
  return (
    typeof window !== "undefined" &&
    "serviceWorker" in navigator &&
    "PushManager" in window &&
    "Notification" in window
  );
}

export function getPushPermission() {
  if (!isPushSupported()) return "unsupported";
  return Notification.permission;
}

/** מבקש הרשאה — חייב להיקרא ישירות מתוך לחיצת משתמש (ללא await לפניו) */
export function requestNotificationPermission() {
  if (!isPushSupported()) {
    return Promise.reject(new Error("הדפדפן לא תומך בהתראות Push"));
  }
  if (Notification.permission === "granted") {
    return Promise.resolve("granted");
  }
  if (Notification.permission === "denied") {
    return Promise.reject(
      new Error(
        "ההתראות חסומות בדפדפן. לחץ על סמל המנעול/הגדרות ליד כתובת האתר והפעל התראות"
      )
    );
  }
  return Notification.requestPermission().then((result) => {
    if (result === "granted") return result;
    if (result === "denied") {
      throw new Error(
        "ההתראות נחסמו. אפשר אותן בהגדרות האתר (סמל המנעול ליד כתובת ה-URL)"
      );
    }
    throw new Error("יש לאשר את חלון ההתראות שקופץ מהדפדפן");
  });
}

async function ensureServiceWorkerRegistration() {
  let registration = await navigator.serviceWorker.getRegistration();

  if (!registration) {
    registration = await navigator.serviceWorker.register("/sw-offline-dev.js", {
      scope: "/",
      updateViaCache: "none",
    });
  }

  if (registration.installing) {
    await new Promise((resolve) => {
      registration.installing.addEventListener("statechange", function onChange() {
        if (this.state === "activated") {
          this.removeEventListener("statechange", onChange);
          resolve();
        }
      });
    });
  } else if (registration.waiting && !registration.active) {
    registration.waiting.postMessage({ type: "SKIP_WAITING" });
    await new Promise((resolve) => {
      navigator.serviceWorker.addEventListener("controllerchange", () => resolve(), { once: true });
    });
  }

  return navigator.serviceWorker.ready;
}

function wrapPushApiError(err) {
  const status = err?.response?.status;
  if (status === 404) {
    return new Error(
      "נתיב Push לא נמצא בשרת — הפעל מחדש את השרת (npm start בתיקיית server)"
    );
  }
  if (status === 503) {
    return new Error("Push לא מוגדר בשרת — חסרים מפתחות VAPID ב-.env");
  }
  return new Error(err?.response?.data?.message || err?.message || "שגיאה בחיבור לשרת");
}

/** רישום Push לאחר שהרשאה כבר ניתנה */
export async function completePushSubscription() {
  let publicKey;
  try {
    ({ publicKey } = await base44.push.getVapidPublicKey());
  } catch (err) {
    throw wrapPushApiError(err);
  }

  if (!publicKey) {
    throw new Error("Push לא מוגדר בשרת — ודא שהשרת רץ עם מפתחות VAPID");
  }

  const registration = await ensureServiceWorkerRegistration();
  let subscription = await registration.pushManager.getSubscription();

  if (!subscription) {
    subscription = await registration.pushManager.subscribe({
      userVisibleOnly: true,
      applicationServerKey: urlBase64ToUint8Array(publicKey),
    });
  }

  try {
    await base44.push.subscribe(subscription.toJSON());
  } catch (err) {
    throw wrapPushApiError(err);
  }

  return subscription;
}

/** @deprecated — השתמש ב-requestNotificationPermission + completePushSubscription */
export async function subscribeToPushNotifications() {
  await requestNotificationPermission();
  return completePushSubscription();
}

export async function unsubscribeFromPushNotifications() {
  if (!isPushSupported()) return;
  const registration = await navigator.serviceWorker.ready;
  const subscription = await registration.pushManager.getSubscription();
  if (subscription) {
    await base44.push.unsubscribe(subscription.endpoint);
    await subscription.unsubscribe();
  }
}

export async function isPushSubscribed() {
  if (!isPushSupported()) return false;
  try {
    const registration = await navigator.serviceWorker.ready;
    const sub = await registration.pushManager.getSubscription();
    return !!sub;
  } catch {
    return false;
  }
}

/** רישום SW מוקדם — לפיתוח, כדי ש-Push יעבוד בלי race עם unregister */
export function registerPushServiceWorkerEarly() {
  if (!isPushSupported()) return;
  navigator.serviceWorker.register("/sw-offline-dev.js", { scope: "/" }).catch(() => {});
}
